import { BottomsheetType } from '@/app/constants/bottomsheeTypes';
import { labels } from '@/app/constants/labels';
import trackEvent, { AnalyticsEvent } from '@/app/lib/analytics';
import { useHashChangeHandler } from '@/app/lib/hooks/useHashChangeHandler';
import Dialog from '@ixigo/iui/Dialog';
import Slide, { type SlideProps } from '@ixigo/iui/Slide';
import Typography from '@ixigo/iui/Typography';
import { useEffect, useState } from 'react';
import type { NetBankingOption } from '../../domain/payment/methods';
import Header from '../Header';
import SearchFilter from '../SearchFilter';
import NetbankingBankingOptions from './NetBankingOptions';
import { filterOptions } from './helpers';

export type AllBanksProps = {
	open: boolean;
	onClose: () => void;
	heading: string;
	options: NetBankingOption[];
	className?: string;
	subText?: string;
	selectDefaultOptionCallback?: () => void;
	showUPISection: boolean;
};

const AllBanks = ({
	open,
	options,
	onClose,
	subText,
	selectDefaultOptionCallback,
	showUPISection,
}: AllBanksProps) => {
	const { NO_BANK_FOUND_TEXT, SEARCH_BANK_PLACEHOLDER_TEXT, NETBANKING_TEXT } = labels;

	const [filteredOptions, setFilteredOptions] = useState<NetBankingOption[]>(options);

	const { closeWithHash } = useHashChangeHandler(open, onClose, 'allBanks', [
		`#${BottomsheetType.PAYMENT_OPTIONS}`,
		'#upi-alert-sheet',
	]);

	const onInputChange = (inputValue: string) => {
		const filtered = filterOptions(inputValue, options);
		setFilteredOptions(filtered);
	};

	useEffect(() => {
		if (selectDefaultOptionCallback && open) {
			selectDefaultOptionCallback();
		}
	}, [selectDefaultOptionCallback, open]);

	useEffect(() => {
		if (open) {
			setFilteredOptions(options);
			if (showUPISection) {
				trackEvent(AnalyticsEvent.NB_BANK_LIST_PAGE_LANDED, null);
			}
		}
	}, [open, options, showUPISection]);

	return (
		<Dialog
			classes={{ container: 'w-full !rounded-none pt-header-height' }}
			fullScreen
			open={open}
			TransitionComponent={Slide}
			TransitionProps={
				{
					direction: 'right',
					timeout: 300,
				} as SlideProps
			}
		>
			<Header headerText={NETBANKING_TEXT} onBack={closeWithHash} subHeaderText={subText} />
			<SearchFilter
				placeholder={SEARCH_BANK_PLACEHOLDER_TEXT}
				onFilterChange={onInputChange}
				className="px-20 pb-15 border-b"
			/>

			{filteredOptions.length !== 0 ? (
				<BanksList
					showUPISection={showUPISection}
					options={options}
					filteredOptions={filteredOptions}
				/>
			) : (
				<Typography variant="body-sm" className="mx-20 mt-20 text-center text-secondary">
					{NO_BANK_FOUND_TEXT}
				</Typography>
			)}
		</Dialog>
	);
};

const BanksList = ({
	options,
	filteredOptions,
	showUPISection,
}: {
	options: NetBankingOption[];
	filteredOptions: NetBankingOption[];
	showUPISection: boolean;
}) => {
	if (filteredOptions.length !== options.length) {
		// When filter is active, show all filtered banks without sections
		return (
			<div className="overflow-y-scroll px-20">
				<NetbankingBankingOptions showUPISection={showUPISection} options={filteredOptions} />
			</div>
		);
	}

	// When no filter, split into Popular and All Banks sections
	const popularBanks = options.slice(0, 5);
	const allBanks = options.slice(5);

	return (
		<div className="overflow-y-scroll px-20">
			<div>
				<Typography variant="body-md" fontWeight="bold" className="mt-15 mb-5">
					Popular Banks
				</Typography>
				<NetbankingBankingOptions showUPISection={showUPISection} options={popularBanks} />
			</div>

			{allBanks.length > 0 && (
				<div>
					<Typography variant="body-md" fontWeight="bold" className="mt-20 mb-5">
						All Banks
					</Typography>
					<NetbankingBankingOptions showUPISection={showUPISection} options={allBanks} />
				</div>
			)}
		</div>
	);
};

export default AllBanks;
